import type { CustomerPartnerDetails, IdCodeName } from '../models/customerPartnerDetails.types';
import type {
  CustomerRequestContact,
  CustomerRequestMasterAccount,
} from '../models/customerWorkspace.types';

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const readString = (obj: Record<string, unknown>, ...keys: string[]): string | undefined => {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return undefined;
};

const nameListFromArray = (items: unknown, nameKeys: string[]): string[] => {
  if (!Array.isArray(items)) {
    return [];
  }
  return items
    .map((item) => {
      if (typeof item === 'string' && item.trim()) {
        return item.trim();
      }
      const rec = asRecord(item);
      if (!rec) {
        return '';
      }
      for (const key of nameKeys) {
        const v = rec[key];
        if (typeof v === 'string' && v.trim()) {
          return v.trim();
        }
      }
      return '';
    })
    .filter(Boolean);
};

const readId = (record: Record<string, unknown>): number => {
  const raw = record.id ?? record.Id;
  const id = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(id) ? id : 0;
};

const normalizeIdCodeNameList = (value: unknown): IdCodeName[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => {
      const rec = asRecord(item);
      if (!rec) {
        return null;
      }
      const code = readString(rec, 'code', 'Code') ?? '';
      const name = readString(rec, 'name', 'Name') ?? '';
      const id = readId(rec);
      if (!id && !code && !name) {
        return null;
      }
      return { id, code, name };
    })
    .filter((item): item is IdCodeName => item != null);
};

export const normalizeCustomerPartnerDetails = (raw: unknown): CustomerPartnerDetails => {
  const record = asRecord(raw) ?? {};
  const master = asRecord(record.masterAccount) ?? asRecord(record.MasterAccount) ?? {};
  const contact = asRecord(record.customerContact) ?? asRecord(record.CustomerContact) ?? {};

  return {
    customerName: readString(record, 'customerName', 'CustomerName') ?? '',
    partnerCode: readString(record, 'partnerCode', 'PartnerCode') ?? '',
    sicCode: readString(record, 'sicCode', 'SicCode') ?? '',
    biRoleName: readString(record, 'biRoleName', 'BiRoleName') ?? '',
    masterAccount: {
      partnerName: readString(master, 'partnerName', 'PartnerName') ?? '',
      billableAccountCode:
        readString(master, 'billableAccountCode', 'BillableAccountCode') ?? '',
      features: normalizeIdCodeNameList(master.features ?? master.Features),
      roles: normalizeIdCodeNameList(master.roles ?? master.Roles),
      tmsCode: readString(master, 'tmsCode', 'TmsCode') ?? null,
      dmsCorpCode: readString(master, 'dmsCorpCode', 'DmsCorpCode') ?? null,
      ein: readString(master, 'ein', 'Ein') ?? null,
    },
    customerContact: {
      firstName: readString(contact, 'firstName', 'FirstName') ?? '',
      lastName: readString(contact, 'lastName', 'LastName') ?? '',
      email: readString(contact, 'email', 'Email') ?? '',
      phoneNumber: readString(contact, 'phoneNumber', 'PhoneNumber', 'phone') ?? '',
    },
  };
};

export const normalizeCustomerContactFromApi = (
  record: Record<string, unknown>
): CustomerRequestContact | undefined => {
  const nested =
    asRecord(record.customerContact) ??
    asRecord(record.CustomerContact) ??
    asRecord(record.contact);

  const source = nested ?? record;
  const firstName = readString(source, 'firstName', 'FirstName');
  const lastName = readString(source, 'lastName', 'LastName');
  const email = readString(source, 'email', 'Email');
  const phoneNumber = readString(source, 'phoneNumber', 'PhoneNumber', 'phone');

  if (!firstName && !lastName && !email && !phoneNumber) {
    return undefined;
  }

  return { firstName, lastName, email, phoneNumber };
};

export const normalizeMasterAccountFromApi = (
  record: Record<string, unknown>
): CustomerRequestMasterAccount | undefined => {
  const nested =
    asRecord(record.masterAccount) ??
    asRecord(record.MasterAccount) ??
    asRecord(record.masteraccount);

  if (!nested) {
    return undefined;
  }

  const partnerName = readString(nested, 'partnerName', 'PartnerName');
  const billableAccountCode = readString(
    nested,
    'billableAccountCode',
    'BillableAccountCode'
  );
  const tmsCode = readString(nested, 'tmsCode', 'TmsCode') ?? null;
  const dmsCorpCode = readString(nested, 'dmsCorpCode', 'DmsCorpCode') ?? null;
  const ein = readString(nested, 'ein', 'Ein', 'einNumber') ?? null;

  let featureNames = nameListFromArray(nested.featureNames ?? nested.FeatureNames, [
    'featureName',
    'name',
  ]);
  if (featureNames.length === 0) {
    featureNames = nameListFromArray(nested.features ?? nested.Features, [
      'featureName',
      'FeatureName',
      'name',
    ]);
  }

  let roleNames = nameListFromArray(nested.roleNames ?? nested.RoleNames, ['roleName', 'name']);
  if (roleNames.length === 0) {
    roleNames = nameListFromArray(nested.roles ?? nested.Roles, [
      'roleName',
      'RoleName',
      'name',
    ]);
  }

  if (
    !partnerName &&
    !billableAccountCode &&
    !tmsCode &&
    !dmsCorpCode &&
    !ein &&
    featureNames.length === 0 &&
    roleNames.length === 0
  ) {
    return undefined;
  }

  return {
    partnerName,
    billableAccountCode,
    tmsCode,
    dmsCorpCode,
    ein,
    ...(featureNames.length ? { featureNames } : {}),
    ...(roleNames.length ? { roleNames } : {}),
  };
};
